import React, { useState, useEffect } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Popconfirm, message, Card, Select, Input, Button, Upload, Modal, Switch, Space, Typography, Progress, Tabs, Tag, Tooltip } from 'antd';
import { 
  DeleteOutlined, 
  UploadOutlined, 
  SettingOutlined, 
  FileOutlined,
  InfoCircleOutlined,
  FileTextOutlined,
  LinkOutlined
} from '@ant-design/icons';
import { Icon } from '@iconify/react';
import NodeInputHandler from '../common/NodeInputHandler';
import { adminInstance } from '../api/axiosConfig';

const { Option } = Select;
const { Text } = Typography;
const { TabPane } = Tabs;
const { Dragger } = Upload;

const languages = [
  { code: 'abq', name: 'Baza' },
  { code: 'ady', name: 'Adyghe' },
  { code: 'af', name: 'Afrikaans' },
  { code: 'ang', name: 'Angika' },
  { code: 'ar', name: 'Arabic' },
  { code: 'as', name: 'Assamese' },
  { code: 'ava', name: 'Avar' },
  { code: 'az', name: 'Azerbaijani' },
  { code: 'be', name: 'Belarusian' },
  { code: 'bg', name: 'Bulgarian' },
  { code: 'bh', name: 'Bihari' },
  { code: 'bho', name: 'Bhojpuri' },
  { code: 'bn', name: 'Bengali' },
  { code: 'bs', name: 'Bosnian' },
  { code: 'ch_sim', name: 'Chinese (Simplified)' },
  { code: 'ch_tra', name: 'Chinese (Traditional)' },
  { code: 'che', name: 'Chechen' },
  { code: 'cs', name: 'Czech' },
  { code: 'cy', name: 'Welsh' },
  { code: 'da', name: 'Danish' },
  { code: 'dar', name: 'Dargwa' },
  { code: 'de', name: 'German' },
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'et', name: 'Estonian' },
  { code: 'fa', name: 'Persian (Farsi)' },
  { code: 'fr', name: 'French' },
  { code: 'ga', name: 'Irish' },
  { code: 'gom', name: 'Goan Konkani' },
  { code: 'hi', name: 'Hindi' },
  { code: 'hr', name: 'Croatian' },
  { code: 'hu', name: 'Hungarian' },
  { code: 'id', name: 'Indonesian' },
  { code: 'inh', name: 'Ingush' },
  { code: 'is', name: 'Icelandic' },
  { code: 'it', name: 'Italian' },
  { code: 'ja', name: 'Japanese' },
  { code: 'kbd', name: 'Kabardian' },
  { code: 'kn', name: 'Kannada' },
  { code: 'ko', name: 'Korean' },
  { code: 'ku', name: 'Kurdish' },
  { code: 'la', name: 'Latin' },
  { code: 'lbe', name: 'Lak' },
  { code: 'lez', name: 'Lezghian' },
  { code: 'lt', name: 'Lithuanian' },
  { code: 'lv', name: 'Latvian' },
  { code: 'mah', name: 'Magahi' },
  { code: 'mai', name: 'Maithili' },
  { code: 'mi', name: 'Maori' },
  { code: 'mn', name: 'Mongolian' },
  { code: 'mr', name: 'Marathi' },
  { code: 'ms', name: 'Malay' },
  { code: 'mt', name: 'Maltese' },
  { code: 'ne', name: 'Nepali' },
  { code: 'new', name: 'Newari' },
  { code: 'nl', name: 'Dutch' },
  { code: 'no', name: 'Norwegian' },
  { code: 'oc', name: 'Occitan' },
  { code: 'pi', name: 'Pali' },
  { code: 'pl', name: 'Polish' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'ro', name: 'Romanian' },
  { code: 'ru', name: 'Russian' },
  { code: 'rs_cyrillic', name: 'Serbian (Cyrillic)' },
  { code: 'rs_latin', name: 'Serbian (Latin)' },
  { code: 'sck', name: 'Nagpuri' },
  { code: 'sk', name: 'Slovak' },
  { code: 'sl', name: 'Slovenian' },
  { code: 'sq', name: 'Albanian' },
  { code: 'sv', name: 'Swedish' },
  { code: 'sw', name: 'Swahili' },
  { code: 'ta', name: 'Tamil' },
  { code: 'tab', name: 'Tabassaran' },
  { code: 'te', name: 'Telugu' },
  { code: 'th', name: 'Thai' },
  { code: 'tjk', name: 'Tajik' },
  { code: 'tl', name: 'Tagalog' },
  { code: 'tr', name: 'Turkish' },
  { code: 'ug', name: 'Uyghur' },
  { code: 'uk', name: 'Ukrainian' },
  { code: 'ur', name: 'Urdu' },
  { code: 'uz', name: 'Uzbek' },
  { code: 'vi', name: 'Vietnamese' },
];

const ParserNode = ({ id, data, isConnectable }) => {
  const [nodeName, setNodeName] = useState(data.nodeName || `Parser ${id}`);
  const [inputType, setInputType] = useState(data.inputType || 'link');
  const [documentLink, setDocumentLink] = useState(data.documentLink || '');
  const [uploadedFiles, setUploadedFiles] = useState(data.uploadedFiles || []);
  const [activeTab, setActiveTab] = useState('input');
  const [parserSettings, setParserSettings] = useState(data.parserSettings || {
    language: 'en',
    parsingInstructions: '',
    skipDiagonalText: false,
    doNotUnrollColumns: false,
    targetPages: '',
  });
  const [parsedContents, setParsedContents] = useState(data.parsedContents || {});
  const [isParsing, setIsParsing] = useState(false);
  const [parsingProgress, setParsingProgress] = useState(0);

  useEffect(() => {
    data.updateNodeData(id, { 
      nodeName, 
      inputType, 
      documentLink, 
      uploadedFiles, 
      parserSettings,
      parsedContents
    });
  }, [nodeName, inputType, documentLink, uploadedFiles, parserSettings, parsedContents, id, data]);

  const handleFileUpload = async (info) => {
    const { file } = info;
    const formData = new FormData();
    formData.append('document', file);

    try {
      const response = await adminInstance.post('/upload-document', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        const newFile = {
          id: response.data.id,
          name: file.name,
          url: response.data.url
        };
        setUploadedFiles(prev => [...prev, newFile]);
        message.success(`${file.name} uploaded successfully`);
      } else {
        message.error(`${file.name} upload failed.`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      message.error(`${file.name} upload failed: ${error.response?.data?.message || error.message}`);
    }
  };

  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
    setParsedContents(prev => {
      const newContents = { ...prev };
      delete newContents[fileId];
      return newContents;
    });
  };

  const parseDocuments = async () => {
    setIsParsing(true);
    setParsingProgress(0);
    const totalFiles = uploadedFiles.length;
    let parsedCount = 0;
    const newParsedContents = { ...parsedContents };

    for (const file of uploadedFiles) {
      if (newParsedContents[file.id]) {
        parsedCount++;
        setParsingProgress((parsedCount / totalFiles) * 100);
        continue;
      }

      try {
        const response = await adminInstance.post('/parse-uploaded-document', {
          document_url: file.url,
          parser_settings: parserSettings
        });
        if (response.data.success) {
          newParsedContents[file.id] = response.data.parsed_content;
          parsedCount++;
          setParsingProgress((parsedCount / totalFiles) * 100);
        } else {
          message.error(`Failed to parse ${file.name}`);
        }
      } catch (error) {
        console.error('Parse error:', error);
        message.error(`Failed to parse ${file.name}: ${error.message}`);
      }
    }

    setParsedContents(newParsedContents);
    setIsParsing(false);
    if (parsedCount === totalFiles) {
      message.success('All documents parsed successfully');
    }
  };

  const renderLinkInput = () => (
    <div style={{ marginBottom: 16 }}>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Input
          prefix={<LinkOutlined />}
          placeholder="Enter document URL"
          value={documentLink}
          onChange={(e) => setDocumentLink(e.target.value)}
          className="nodrag"
        />
        <NodeInputHandler 
          nodeId={id} 
          onInsert={(tag) => setDocumentLink(prev => prev + tag)}
        />
      </Space>
    </div>
  );

  const renderFileUpload = () => (
    <div style={{ marginBottom: 16 }}>
      <Dragger
        name="document"
        multiple={true}
        customRequest={({ file, onSuccess }) => {
          handleFileUpload({ file }).then(() => onSuccess());
        }}
        className="nodrag"
        showUploadList={false}
      >
        <p className="ant-upload-drag-icon">
          <UploadOutlined style={{ fontSize: 24, color: '#1890ff' }} />
        </p>
        <p className="ant-upload-text">Click or drag files to upload</p>
        <p className="ant-upload-hint">
          Supports PDF, DOC, DOCX, TXT, and most other file types
        </p>
      </Dragger>

      {uploadedFiles.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Text strong>Uploaded Files:</Text>
          {uploadedFiles.map(file => (
            <div key={file.id} style={{ 
              padding: '8px',
              marginTop: 8,
              background: '#f5f5f5',
              borderRadius: '6px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Space>
                <FileOutlined />
                <Text>{file.name}</Text>
              </Space>
              <Button 
                type="text" 
                danger 
                icon={<DeleteOutlined />}
                onClick={() => removeFile(file.id)}
              />
            </div>
          ))}

          <Button 
            type="primary"
            icon={<FileTextOutlined />}
            onClick={parseDocuments}
            loading={isParsing}
            style={{ marginTop: 16, width: '100%' }}
          >
            Parse Documents
          </Button>

          {isParsing && (
            <Progress 
              percent={parsingProgress} 
              status="active" 
              style={{ marginTop: 16 }}
            />
          )}
        </div>
      )}
    </div>
  );

  return (
    <Card 
      title={
        <Space>
          <Icon icon="mdi:file-document-check" style={{ fontSize: '18px', color: '#1890ff' }}/>
          <span>Document Parser</span>
          <Tag color="blue">LlamaParse</Tag>
        </Space>
      }
      style={{ width: 380 }}
      extra={
        <Space>
          <Button 
            icon={<SettingOutlined />}
            type="text"
            onClick={() => setActiveTab('settings')}
          />
      <Popconfirm
        title="Delete Node"
        description="Are you sure you want to delete this node?"
        onConfirm={() => data.onDelete(id)}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }} > 
        <Button icon={<DeleteOutlined />} /> </Popconfirm>
        </Space>
      }
    >
      <Handle type="target" position={Position.Left} isConnectable={isConnectable} />
      
      <Input
        placeholder="Node Name"
        value={nodeName}
        onChange={(e) => setNodeName(e.target.value)}
        style={{ marginBottom: 16 }}
        prefix={<InfoCircleOutlined style={{ color: '#bfbfbf' }} />}
        className="nodrag"
      />

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Input" key="input">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Select
              style={{ width: '100%', marginBottom: 16 }}
              value={inputType}
              onChange={setInputType}
              className="nodrag"
            >
              <Option value="link">Document URL</Option>
              <Option value="upload">File Upload</Option>
            </Select>

            {inputType === 'link' ? renderLinkInput() : renderFileUpload()}
          </Space>
        </TabPane>

        <TabPane tab="Settings" key="settings">
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <Space align="center">
                <Text strong>Language</Text>
                <Tooltip title="Select the primary language of your documents">
                  <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
                </Tooltip>
              </Space>
              <Select
                style={{ width: '100%', marginTop: 4 }}
                value={parserSettings.language}
                onChange={(value) => setParserSettings({ ...parserSettings, language: value })}
                className="nodrag"
                showSearch
                optionFilterProp="children"
              >
                {languages.map(lang => (
                  <Option key={lang.code} value={lang.code}>{lang.name}</Option>
                ))}
              </Select>
            </div>

            <div>
              <Space align="center">
                <Text strong>Parsing Instructions</Text>
                <Tooltip title="Optional instructions to guide the parsing process">
                  <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
                </Tooltip>
              </Space>
              <Input.TextArea
                placeholder="Enter specific parsing instructions..."
                value={parserSettings.parsingInstructions}
                onChange={(e) => setParserSettings({ ...parserSettings, parsingInstructions: e.target.value })}
                style={{ marginTop: 4 }}
                rows={3}
                className="nodrag"
              />
            </div>

            <div>
              <Space align="center">
                <Text strong>Advanced Options</Text>
                <Tooltip title="Configure additional parsing behavior">
                  <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
                </Tooltip>
              </Space>
              <div style={{ 
                marginTop: 8,
                padding: 12,
                background: '#f5f5f5',
                borderRadius: 6
              }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text>Skip Diagonal Text</Text>
                    <Switch
                      size="small"
                      checked={parserSettings.skipDiagonalText}
                      onChange={(checked) => setParserSettings({ ...parserSettings, skipDiagonalText: checked })}
                    />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text>Preserve Columns</Text>
                    <Switch
                      size="small"
                      checked={parserSettings.doNotUnrollColumns}
                      onChange={(checked) => setParserSettings({ ...parserSettings, doNotUnrollColumns: checked })}
                    />
                  </div>
                </Space>
              </div>
            </div>

            <div>
              <Space align="center">
                <Text strong>Target Pages</Text>
                <Tooltip title="Specify pages to parse (e.g., 1,3-5,7)">
                  <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
                </Tooltip>
              </Space>
              <Input
                placeholder="e.g., 1,3-5,7"
                value={parserSettings.targetPages}
                onChange={(e) => setParserSettings({ ...parserSettings, targetPages: e.target.value })}
                style={{ marginTop: 4 }}
                className="nodrag"
              />
            </div>
          </Space>
        </TabPane>

        <TabPane tab="Output" key="output">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text type="secondary">
              The parser node provides the following outputs:
            </Text>
            <div style={{ 
              backgroundColor: '#f5f5f5', 
              padding: '12px', 
              borderRadius: '6px',
              marginTop: '8px'
            }}>
              <Space direction="vertical">
                <div>
                  <Text strong>Parsed Content</Text>
                  <div>
                    <Text code>[Input from {id}]</Text>
                  </div>
                </div>
              </Space>
            </div>
          </Space>
        </TabPane>
      </Tabs>

      <Handle type="source" position={Position.Right} id="a" isConnectable={isConnectable} />
    </Card>
  );
};

export default ParserNode;